<template>
  <v-container fluid pt-0>
    <v-row align="center" justify="end">
      <v-col cols="3">
        <dates-range-picker
          dense
          :gt-date-from-string="dateFromFilter"
          :gt-date-to-string="dateToFilter"
          :outlined="outlinedInputs"
          @onRangeSelected="onDatesChange"
        ></dates-range-picker>
      </v-col>
      <v-col>
        <gt-multi-select
          :items="sortedServices"
          label="Service Filter"
          item-text="node.name"
          item-value="node.id"
          v-model="serviceFilter"
          :outlined="outlinedInputs"
          dense
        ></gt-multi-select>
      </v-col>
      <v-col>
        <v-select
          :items="daysOfWeek"
          item-text="name"
          item-value="value"
          label="Day of Week Filter"
          v-model="dayOfWeekFilter"
          multiple
          :outlined="outlinedInputs"
          hide-details
          dense
        />
      </v-col>
      <v-col>
        <v-text-field
          label="Name Filter"
          v-model="nameInputFilter"
          @blur="onNameFilterBlur"
          clearable
          dense
          :outlined="outlinedInputs"
          hide-details
        />
      </v-col>
      <v-col sm="1" class="py-0">
        <v-select
          v-if="showGroupedView"
          :items="groupByOptions"
          v-model="selectedGroupedBy"
          label="Group by"
          item-text="name"
          return-object
          :outlined="outlinedInputs"
          hide-details
          dense
        ></v-select>
      </v-col>
      <v-col>
        <v-switch
          v-model="serviceNotInUseFilter"
          label="Not In Use"
          dense
        ></v-switch>
      </v-col>
      <v-switch
        prepend-icon="mdi-view-list"
        class="mr-3"
        label="Group View"
        v-model="showGroupedView"
        @change="onShowGroupedViewChange"
      ></v-switch>
      <div class="pr-2">
        <list-visible-columns-and-filter
          :visible-columns="visibleColumns"
          :selectable-headers="selectableHeaders"
          v-model="search"
          @change="onVisibleColumnsChange"
        ></list-visible-columns-and-filter>
      </div>
      <add-closeouts-dialog :services="services" @create="onCloseoutsCreate">
        <template v-slot:activator="{ on }">
          <v-btn
            v-on="on"
            color="primary"
            :disabled="$store.getters.hasAccessTo('suppliers:view_only')"
          >
            <v-icon left>mdi-plus-circle</v-icon>Add Closeouts
          </v-btn>
        </template>
      </add-closeouts-dialog>
    </v-row>

    <v-row v-if="showGroupedView">
      <v-col class="pa-0">
        <list-grouped :group="selectedGroupedBy" :items="closeouts" panels>
          <template v-slot:table="{ items }">
            <v-card outlined>
              <closeouts-table
                :items="items"
                :headers="filteredHeaders"
                :search="search"
                :hide-footer="true"
                @delete="onCloseoutDelete"
                @edit="onCloseoutEdit"
                @deleteRecords="onDeleteRecords"
              ></closeouts-table>
            </v-card>
          </template>
        </list-grouped>
      </v-col>
    </v-row>
    <v-row v-else>
      <v-col class="pa-0">
        <v-card outlined>
          <closeouts-table
            :items="closeouts"
            :headers="filteredHeaders"
            :search="search"
            @delete="onCloseoutDelete"
            @edit="onCloseoutEdit"
            @deleteRecords="onDeleteRecords"
          ></closeouts-table>
        </v-card>
      </v-col>
    </v-row>
    <v-row v-if="debug">
      <pre>{{ dayOfWeekFilter }}</pre>
    </v-row>
    <closeout-item-editor
      v-model="editedItem"
      :show="showCloseoutItemEditor"
      @input="onCloseoutItemSave"
      @close="showCloseoutItemEditor = false"
    />
  </v-container>
</template>

<script>
import {
  removeGQLTypename,
  sortByDate,
  sortByDateField,
} from "../../../../helpers/index";
import ListVisibleColumnsAndFilter from "../../shared/ListVisibleColumnsAndFilter";
import ListGrouped from "../../shared/ListGrouped";
import CloseoutsTable from "./CloseoutsTable";
import AddCloseoutsDialog from "./AddCloseoutsDialog";
import { ListMixin } from "../../../../mixins/ListMixin";
import CloseoutItemEditor from "../allocations/AllocationItemEditor";
import { REMOVE_RECORDS } from "../../../../constants/graphql";
import gql from "graphql-tag";
import DatesRangePicker from "../../shared/DatesRangePicker.vue";
import GtMultiSelect from "../../shared/GtMultiSelect.vue";
import EventBus from "../../../../store/event-bus";
const moment = require("moment");
const QUERY = gql`
  query supplierCloseouts(
    $supplierId: ID!
    $filter: SupplierCloseoutFilterType
  ) {
    supplier(id: $supplierId) {
      id
      closeouts(filter: $filter) {
        totalCount
        edges {
          node {
            id
            name
            date
            type
            releaseDate
            uid
            releaseDays
            dayOfWeek
            groupId
            taken
            qty
            max1
            max2
            max3
            max4
            max5
            max6
            max7
            maxTotal
            priority
            agentGroup {
              name
            }
            service {
              name
            }
          }
        }
      }
    }
  }
`;
export const ADD_CLOSEOUTS_MUTATION = gql`
  mutation (
    $supplierId: ID!
    $supplierAddCloseoutsInput: SupplierAddCloseoutsInput
  ) {
    supplierAddCloseouts(
      supplierId: $supplierId
      supplierAddCloseoutsInput: $supplierAddCloseoutsInput
    ) {
      success
      error
    }
  }
`;
export const DELETE_CLOSEOUT_MUTATION = gql`
  mutation ($id: ID!) {
    deleteCloseout(id: $id) {
      id
    }
  }
`;
export const UPDATE_CLOSEOUT_MUTATION = gql`
  mutation ($id: ID!, $closeoutInput: CloseoutInput) {
    updateCloseout(id: $id, closeoutInput: $closeoutInput) {
      id
    }
  }
`;
export default {
  name: "supplier-closeouts",
  props: {
    showTitle: {
      type: Boolean,
      default: () => true,
    },
    supplierId: {
      type: String,
      default: "",
    },
    services: {
      type: Array,
      default: () => [],
    },
  },
  components: {
    ListVisibleColumnsAndFilter,
    ListGrouped,
    CloseoutsTable,
    AddCloseoutsDialog,
    CloseoutItemEditor,
    DatesRangePicker,
    GtMultiSelect,
  },
  mixins: [ListMixin],
  data() {
    return {
      debug: false,
      showGroupedView: this.$store.getters.localSettings(
        `${this.$options.name}-showGroupedView`,
        false
      ),
      search: "",
      visibleColumns: this.$store.getters.localSettings(this.$options.name, [
        "date",
        "name",
        "agentGroupName",
        "service",
        "qty",
      ]),
      headers: [
        {
          text: "Date",
          sortable: true,
          sort: sortByDate,
          value: "date",
          name: "date",
        },
        {
          text: "Name",
          sortable: true,
          value: "name",
          name: "name",
        },
        {
          text: "Agent/Group",
          sortable: true,
          value: "agentGroupName",
          name: "agentGroupName",
        },
        {
          text: "Service",
          sortable: true,
          value: "service.name",
          name: "service",
        },
        {
          text: "Closeout",
          sortable: true,
          value: "closed",
          name: "cosed",
          align: "center",
        },

        {
          text: "Actions",
          sortable: false,
          value: "actions",
          name: "actions",
          align: "center",
        },
      ],
      selectedGroupedBy: {
        name: "Service",
        getKey: (item) => item.service.name,
        getLabel: (item) => item.service.name,
      },
      groupByOptions: [
        {
          name: "Service",
          getKey: (item) => item.service.name,
          getLabel: (item) => item.service.name,
        },
        {
          name: "Close Out Name",
          getKey: (item) => item.name,
          getLabel: (item) => item.name,
        },
        {
          name: "Agent/Group",
          getKey: (item) => item.groupId,
          getLabel: (item) => item.agentGroupName,
        },
        {
          name: "Date Range",
          getKey: (item) => item.uid,
          getLabel: (item) => {
            const rangeItems = this.closeouts
              .filter((i) => i.uid == item.uid)
              .sort(sortByDateField("date"));
            return `${
              rangeItems[0].name
            } - from: ${this.$options.filters.formatDate(
              rangeItems[0].date
            )}, to: ${this.$options.filters.formatDate(
              rangeItems[rangeItems.length - 1].date
            )}`;
          },
        },
      ],
      editedItem: null,
      showCloseoutItemEditor: false,
      dateFromFilter: new Date().toISOString(),
      dateToFilter: moment().add(1, "year").toISOString(false),
      serviceFilter: [],
      nameInputFilter: "",
      nameFilter: "",
      daysOfWeek: [
        { name: "Monday", value: 1 },
        { name: "Tuesday", value: 2 },
        { name: "Wednesday", value: 3 },
        { name: "Thursday", value: 4 },
        { name: "Friday", value: 5 },
        { name: "Saturday", value: 6 },
        { name: "Sunday", value: 7 },
      ],
      dayOfWeekFilter: [],
      serviceNotInUseFilter: false,
    };
  },

  computed: {
    sortedServices() {
      return this.services.sort((a, b) =>
        a.node.name.localeCompare(b.node.name)
      );
    },

    outlinedInputs() {
      return this.$store.getters.localSettings("outlinedInputs");
    },
    closeouts() {
      return this.supplier && this.supplier.closeouts
        ? [
            ...this.supplier.closeouts.edges
              .map(removeGQLTypename)
              .sort(sortByDateField("date"))
              .filter((item) => item.qty > 0)
              .map((item) => ({
                ...item,
                agentGroupName: item.agentGroup
                  ? item.agentGroup.name
                  : "All Groups",
                dayOfWeekLabel: moment(item.date).format("ddd"),
                closed: item.qty > 0,
              })),
          ]
        : [];
    },
  },

  methods: {
    onNameFilterBlur(e) {
      if (this.nameInputFilter != "") {
        this.nameFilter = this.nameInputFilter;
      } else {
        this.nameFilter = "";
      }
    },
    onDatesChange(payload) {
      this.dateFromFilter = payload.dateFrom;
      this.dateToFilter = payload.dateTo;
    },
    onShowGroupedViewChange(event) {
      this.$store.dispatch("updateLocalSettings", {
        [`${this.$options.name}-showGroupedView`]: event,
      });
    },
    onCloseoutEdit(data) {
      this.editedItem = { ...data };
      this.showCloseoutItemEditor = true;
    },
    async onCloseoutItemSave(payload) {
      this.showCloseoutItemEditor = false;
      try {
        const result = await this.$apollo.mutate({
          mutation: UPDATE_CLOSEOUT_MUTATION,
          variables: {
            id: this.editedItem.id,
            closeoutInput: {
              qty: parseInt(this.editedItem.qty),
              releaseDays: parseInt(this.editedItem.releaseDays),
              maxTotal: parseFloat(this.editedItem.maxTotal),
            },
          },
          refetchQueries: [
            { query: QUERY, variables: { supplierId: this.supplierId } },
          ],
        });
        this.$store.dispatch("showAppSnackbar", {
          msg: "Closeout updated",
        });
        //console.log(result);
      } catch (error) {
        console.log(error);
      }
    },
    async onCloseoutDelete(data) {
      try {
        const result = await this.$apollo.mutate({
          mutation: DELETE_CLOSEOUT_MUTATION,
          variables: {
            id: data.id,
          },
          refetchQueries: [
            { query: QUERY, variables: { supplierId: this.supplierId } },
          ],
        });
        this.$store.dispatch("showAppSnackbar", {
          msg: "Closeout removed",
        });
      } catch (error) {
        console.log(error);
      }
    },
    async onCloseoutsCreate(data) {
      try {
        const result = await this.$apollo.mutate({
          mutation: ADD_CLOSEOUTS_MUTATION,
          variables: {
            supplierId: this.supplierId,
            supplierAddCloseoutsInput: data,
          },
          refetchQueries: [
            {
              query: QUERY,
              variables: {
                supplierId: this.supplierId,
                filter: {
                  date_gte:
                    this.dateFromFilter != "" ? this.dateFromFilter : undefined,
                  date_lte:
                    this.dateToFilter != "" ? this.dateToFilter : undefined,
                  services: this.serviceFilter.length
                    ? this.serviceFilter
                    : undefined,
                  name_contains:
                    this.nameFilter != "" ? this.nameFilter : undefined,
                  day_of_week_eq: this.dayOfWeekFilter.length
                    ? this.dayOfWeekFilter
                    : undefined,
                  serviceNotInUse: this.serviceNotInUseFilter,
                },
              },
            },
          ],
        });
        this.$store.dispatch("showAppSnackbar", {
          msg: "Closeouts added",
        });
        console.log(result);
      } catch (error) {
        console.log(error);
      }
    },
    async onDeleteRecords(payload) {
      try {
        const resp = await this.$apollo.mutate({
          mutation: REMOVE_RECORDS,
          variables: { input: payload.map((item) => item.id) },
          refetchQueries: [
            {
              query: QUERY,
              variables: {
                supplierId: this.supplierId,
                filter: {
                  date_gte:
                    this.dateFromFilter != "" ? this.dateFromFilter : undefined,
                  date_lte:
                    this.dateToFilter != "" ? this.dateToFilter : undefined,
                  services: this.serviceFilter.length
                    ? this.serviceFilter
                    : undefined,
                  name_contains:
                    this.nameFilter != "" ? this.nameFilter : undefined,
                  day_of_week_eq: this.dayOfWeekFilter.length
                    ? this.dayOfWeekFilter
                    : undefined,
                  serviceNotInUse: this.serviceNotInUseFilter,
                },
              },
            },
          ],
        });
        this.$store.dispatch("showAppSnackbar", {
          msg: "Closeouts removed",
        });
      } catch (error) {
        console.error(error);
      }
    },
  },
  apollo: {
    supplier: {
      query: QUERY,
      variables() {
        return {
          supplierId: this.supplierId,
          filter: {
            date_gte:
              this.dateFromFilter != "" ? this.dateFromFilter : undefined,
            date_lte: this.dateToFilter != "" ? this.dateToFilter : undefined,
            services: this.serviceFilter.length
              ? this.serviceFilter
              : undefined,
            name_contains: this.nameFilter != "" ? this.nameFilter : undefined,
            day_of_week_eq: this.dayOfWeekFilter.length
              ? this.dayOfWeekFilter
              : undefined,
            serviceNotInUse: this.serviceNotInUseFilter,
          },
        };
      },
      fetchPolicy: "network-only",
      skip() {
        return !this.supplierId;
      },
    },
  },
  mounted() {
    EventBus.$on("supplier-services-change", () => {
      if (this.$apollo.queries.supplier != null) {
        this.$apollo.queries.supplier.refetch();
      }
    });
  },
};
</script>

<style></style>



// WEBPACK FOOTER //
// src/components/gt/suppliers/closeouts/SupplierCloseouts.vue